import React, { useState, useRef } from 'react';

const STORAGE_KEY = 'projectTabsOrder_v1';

// Drag-and-drop reorderable tabs bar. Order is persisted in localStorage so
// it applies to ALL projects (current and future).
export const ProjectTabsBar = ({ tabs, activeTab, setActiveTab }) => {
  const [order, setOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const dragKey = useRef(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  const persist = (newOrder) => {
    setOrder(newOrder);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(newOrder)); } catch {}
  };

  // Apply persisted order, then append any new tabs at the end
  const orderedTabs = (() => {
    if (!order || order.length === 0) return tabs;
    const map = new Map(tabs.map(t => [t.key, t]));
    const result = [];
    order.forEach(k => { if (map.has(k)) { result.push(map.get(k)); map.delete(k); } });
    map.forEach(v => result.push(v));
    return result;
  })();

  const onDragStart = (e, key) => {
    dragKey.current = key;
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', key); } catch {}
    e.currentTarget.style.opacity = '0.55';
  };
  const onDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverKey !== key) setDragOverKey(key);
  };
  const onDragLeave = () => setDragOverKey(null);
  const onDrop = (e, targetKey) => {
    e.preventDefault();
    setDragOverKey(null);
    const sourceKey = dragKey.current;
    if (!sourceKey || sourceKey === targetKey) return;
    const current = orderedTabs.map(t => t.key);
    const fromIdx = current.indexOf(sourceKey);
    const toIdx = current.indexOf(targetKey);
    if (fromIdx === -1 || toIdx === -1) return;
    const newOrder = [...current];
    const [moved] = newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, moved);
    persist(newOrder);
  };
  const onDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    dragKey.current = null;
    setDragOverKey(null);
  };

  return (
    <div data-testid="project-tabs-bar" style={{ display: 'flex', gap: '0', borderBottom: '2px solid #E2E2E6', marginBottom: '24px', overflowX: 'auto' }}>
      {orderedTabs.map((tab) => (
        <button
          key={tab.key}
          draggable
          onDragStart={(e) => onDragStart(e, tab.key)}
          onDragOver={(e) => onDragOver(e, tab.key)}
          onDragLeave={onDragLeave}
          onDrop={(e) => onDrop(e, tab.key)}
          onDragEnd={onDragEnd}
          onClick={() => setActiveTab(tab.key)}
          data-testid={`project-tab-${tab.key}`}
          style={{
            padding: '12px 24px',
            border: 'none',
            backgroundColor: dragOverKey === tab.key ? '#E3F2FD' : 'transparent',
            cursor: 'grab',
            fontWeight: activeTab === tab.key ? '700' : '400',
            borderBottom: activeTab === tab.key ? '2px solid #002FA7' : '2px solid transparent',
            marginBottom: '-2px',
            color: activeTab === tab.key ? '#002FA7' : '#5E5F66',
            whiteSpace: 'nowrap',
            transition: 'background-color 0.15s',
          }}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export default ProjectTabsBar;
